import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader } from "./shared";
import { TrendingUp, TrendingDown, Minus, Save, Copy, Users, Calendar, FolderKanban, Mail, Eye, QrCode, MessageSquare, Phone, Loader2 } from "lucide-react";

const MANUAL_META = {
  website_visitors: { label: "Website visitors", icon: Eye },
  qr_scans: { label: "QR scans", icon: QrCode },
  ai_chats: { label: "AI conversations", icon: MessageSquare },
  calls: { label: "Calls", icon: Phone },
};
const AUTO_ICONS = { leads: Users, bookings: Calendar, projects: FolderKanban, signups: Mail };

function monthOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleString(undefined, { month: "long", year: "numeric" });

function Delta({ value, prev }) {
  const d = value - prev;
  if (d === 0 || prev === 0 && value === 0) return <span className="text-gray-500 text-xs flex items-center gap-0.5"><Minus size={11} /> no change</span>;
  const up = d > 0;
  return (
    <span className={`text-xs flex items-center gap-0.5 ${up ? "text-emerald-400" : "text-red-400"}`}>
      {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {up ? "+" : ""}{d} vs last month
    </span>
  );
}

export function GrowthMetrics() {
  const [month, setMonth] = useState(monthOptions()[0]);
  const [data, setData] = useState(null);
  const [manual, setManual] = useState({ website_visitors: 0, qr_scans: 0, ai_chats: 0, calls: 0 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get(`/admin/growth-metrics?month=${month}`)
      .then((r) => { setData(r.data); setManual(r.data.manual); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const saveManual = async () => {
    setSaving(true);
    try {
      await api.put("/admin/growth-metrics/manual", { month, ...Object.fromEntries(Object.entries(manual).map(([k, v]) => [k, parseInt(v) || 0])) });
      toast.success("Metrics saved.");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const summary = () => {
    if (!data) return "";
    return `In ${monthLabel(month)}: ${manual.website_visitors} website visitors, ${manual.qr_scans} QR scans, ${manual.ai_chats} AI conversations, and ${data.auto.bookings.value} booked appointment${data.auto.bookings.value === 1 ? "" : "s"}.`;
  };
  const copySummary = () => { navigator.clipboard.writeText(summary()); toast.success("Summary copied."); };

  const INPUT = "w-full bg-navy border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none";

  return (
    <div data-testid="admin-growth-metrics">
      <Head title="Growth Metrics" desc="Your funnel at a glance. Bookings, leads, projects and signups are counted automatically; type in the numbers that come from outside (visitors, QR scans, AI chats, calls)." />

      <div className="flex items-center gap-3 mb-6">
        <label className="text-sm text-gray-400">Month</label>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="growth-month" className="bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none">
          {monthOptions().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
      </div>

      {!data ? <Loader /> : (
        <>
          {/* Auto metrics */}
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold mb-3">Automatic — from your platform</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {Object.entries(data.auto).map(([key, m]) => {
              const Icon = AUTO_ICONS[key] || Users;
              return (
                <div key={key} className="bg-navy-800 border border-white/10 p-5" data-testid={`growth-auto-${key}`}>
                  <div className="flex items-center gap-2 text-gray-400 mb-3"><Icon size={16} className="text-gold" /><span className="text-sm">{m.label}</span></div>
                  <div className="text-3xl font-heading text-white">{m.value}</div>
                  <div className="mt-1"><Delta value={m.value} prev={m.prev} /></div>
                </div>
              );
            })}
          </div>

          {/* Manual metrics */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Manual — from outside tools</div>
            <button onClick={saveManual} disabled={saving} data-testid="growth-save"
              className="inline-flex items-center gap-2 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {Object.entries(MANUAL_META).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <div key={key} className="bg-navy-800 border border-white/10 p-5" data-testid={`growth-manual-${key}`}>
                  <div className="flex items-center gap-2 text-gray-400 mb-3"><Icon size={16} className="text-gold" /><span className="text-sm">{meta.label}</span></div>
                  <input type="number" min="0" value={manual[key]} data-testid={`growth-input-${key}`}
                    onChange={(e) => setManual((s) => ({ ...s, [key]: e.target.value }))} className={INPUT} />
                </div>
              );
            })}
          </div>

          {/* Client-ready summary */}
          <div className="bg-navy-700 border border-gold/30 p-6" data-testid="growth-summary">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] uppercase tracking-[0.2em] text-gold">Client-ready summary</span>
              <button onClick={copySummary} data-testid="growth-copy" className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-gold transition-colors"><Copy size={14} /> Copy</button>
            </div>
            <p className="text-white text-lg leading-relaxed">{summary()}</p>
          </div>
        </>
      )}
    </div>
  );
}
